import React, { FC, useContext, useEffect, useMemo } from 'react';
import Typography from '@material-ui/core/Typography';
import {
  clearLocalStorage,
  isAuthenticated,
} from '../../../services/localstorage';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import { useApolloClient, useLazyQuery } from '@apollo/react-hooks';
import PowerIcon from '@material-ui/icons/PowerSettingsNew';
import SettingsIcon from '@material-ui/icons/Settings';
import MoreVertIcon from '@material-ui/icons/ArrowDropDown';
import MenuIcon from '@material-ui/icons/Menu';

import { GET_ME } from '../../../graphql/user/query';
import Box from '@material-ui/core/Box';
import useStyles from './styles';
import { Redirect } from 'react-router-dom';
import {
  displaySnackBar,
  SnackVariableInterface,
} from '../../../common/displaySnackbar';
import { ApiErrorCode, Operation, Resource } from '../../../common/constants';
import { IUserContext, UserContext } from '../../../providers/UserProvider';
import { GetMe } from '../../../graphql/user/types/GetMe';
import { Tooltip } from '@material-ui/core';
import RoleManager from '../../RoleManager';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import { formatName } from '../../../utils/common';
import IconButton from '@material-ui/core/IconButton';
import Avatar from '@material-ui/core/Avatar';
import Popover from '@material-ui/core/Popover';
import Divider from '@material-ui/core/Divider';
import clsx from 'clsx';

interface INavHead {
  drawerOpened: boolean;
  onOpenDrawer: () => void;
}

const NavHead: FC<INavHead> = (props) => {
  const { drawerOpened, onOpenDrawer } = props;

  const snackBarData: SnackVariableInterface = {
    type: 'ERROR',
    message: '',
    isOpen: true,
  };

  const { me, setMe, canI } = useContext<IUserContext>(UserContext);

  const client = useApolloClient();
  const [openRoleModal, setOpenRoleModal] = React.useState<boolean>(false);

  const [getMe, { loading, error }] = useLazyQuery<GetMe>(GET_ME, {
    onError: (error) => {
      snackBarData.message = "Une erreur inconnue s'est produite."; // TODO: internationalisation

      if (error.graphQLErrors) {
        if (error.graphQLErrors.length) {
          const currentError = error.graphQLErrors[0];

          if (currentError.extensions && currentError.extensions.code) {
            switch (currentError.extensions.code) {
              case ApiErrorCode.INTERNAL_SERVER_ERROR:
                snackBarData.message = 'Erreur interne du serveur.';
                break;

              case ApiErrorCode.INVALID_TOKEN:
              case ApiErrorCode.BAD_USER_INPUT:
                return;

              case ApiErrorCode.UNAUTHENTICATED:
              case ApiErrorCode.USER_NOT_FOUND:
                snackBarData.message =
                  'Vous devez vous connecter pour effectuer cette opération.';
                break;

              // case UKNOWN_ERROR:
              //   break;
            }
          }
        } else if (error.networkError) {
          snackBarData.message =
            'Erreur de réseau. Veuillez vérifier votre connexion internet.';
        }
      }

      displaySnackBar(client, snackBarData);
    },
    onCompleted: async (data) => {
      if (data?.me?.id) {
        setMe({
          id: data?.me?.id ?? null,
          email: data?.me?.email ?? '',
          firstName: data?.me?.firstName ?? '',
          lastName: data?.me?.lastName ?? '',
          avatarUrl: data?.me?.avatarUrl ?? '',
          recordDirectoryToken: data?.me?.recordDirectoryToken ?? '',
          roles: data?.me?.roles,
          permissions:
            data && data.me && data.me.permissions ? data.me.permissions : [],
        });
      }
    },
  });

  const classes = useStyles();

  const menus = useMemo(() => {
    const _menus: Menu[] = [];
    if (canI(Operation.READ, Resource.ROLES))
      _menus.push({
        label: 'Gérer les rôles',
        icon: <SettingsIcon fontSize="small" />,
        onClick: () => {
          setOpenRoleModal(true);
        },
      });

    _menus.push({
      label: 'Déconnexion',
      icon: <PowerIcon fontSize="small" />,
      onClick: () => {
        clearLocalStorage();
        window.location.reload();
      },
    });
    return _menus;
  }, [canI]);

  useEffect(() => {
    if (isAuthenticated()) {
      getMe();
    }
  }, [getMe]);

  if (loading) return <p>Chargement ...</p>;

  if (error) return <p>Aucune donnée disponible</p>;

  if (!isAuthenticated()) {
    return <Redirect {...{ to: '/login' }} />;
  }

  return (
    <AppBar position="fixed" elevation={1}>
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={onOpenDrawer}
          edge="start"
          className={clsx(classes.menuButton, drawerOpened && classes.hide)}>
          <MenuIcon />
        </IconButton>
        <Box className={classes.rightWrapper}>
          <Box display="flex" justifyContent="center" alignItems="center">
            <Avatar src={me?.avatarUrl} />
            {/* <Typography className={classes.fullName}>
              {formatName({ firstName: me?.firstName, lastName: me?.lastName })}
            </Typography> */}
          </Box>
          <CustomMenu
            avatarUrl={me?.avatarUrl}
            title={formatName({
              firstName: me?.firstName,
              lastName: me?.lastName,
            })}
            menus={menus}
          />
        </Box>
      </Toolbar>
      <RoleManager
        onCloseClick={() => setOpenRoleModal(false)}
        open={openRoleModal}
      />
    </AppBar>
  );
};

type Menu = {
  label: string;
  icon: React.ReactElement;
  onClick: () => void;
};

const CustomMenu: React.FC<{
  title: string;
  avatarUrl: string;
  menus: Menu[];
}> = (props) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  return (
    <div>
      <Tooltip title="Menu">
        <IconButton aria-describedby={id} color="inherit" onClick={handleClick}>
          <MoreVertIcon />
        </IconButton>
      </Tooltip>
      <Popover
        id={id}
        anchorEl={anchorEl}
        keepMounted
        open={open}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handleClose}>
        <Box style={{ padding: 18 }}>
          <Typography
            variant="subtitle1"
            color="inherit"
            style={{ color: 'grey' }}>
            {props?.title}
          </Typography>
          <Divider style={{ marginTop: 12, marginBottom: 12 }} />
          {props.menus.map((menu) => (
            <MenuItem
              key={menu.label}
              onClick={() => {
                handleClose();
                menu.onClick();
              }}>
              <ListItemIcon>{menu.icon}</ListItemIcon>
              <Typography style={{ marginLeft: -30 }} variant="inherit">
                {menu.label}
              </Typography>
            </MenuItem>
          ))}
        </Box>
      </Popover>
    </div>
  );
};

export default NavHead;
